import { useEffect, useRef } from 'react';

import type { AudioEngine } from '../../audio/engine';
import {
  BAR_COUNT,
  columnLayout,
  SpectrumAnalyzer,
  toDisplayColumns,
  type SpectrumParams,
} from '../../audio/spectrum';

/** On-screen pitch of one spectrum column, so a wider strip shows more bars. */
const COLUMN_PITCH = 5;

type SpectrumStripProps = {
  engine: AudioEngine | null;
  params: SpectrumParams;
  playing: boolean;
  /** Dock variant: a shorter strip. Same columns, same peak caps. */
  compact?: boolean;
};

/**
 * The live FFT strip from the Rust player's `spectrum.rs`, drawn above the waveform.
 *
 * Purely cosmetic. When there is no audio graph — no `AudioContext`, or CORS-tainted
 * media — the analyser is simply never fed and the strip renders empty, which is the
 * intended degradation rather than a failure.
 */
export function SpectrumStrip({ engine, params, playing, compact = false }: SpectrumStripProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyzerRef = useRef<SpectrumAnalyzer | null>(null);
  // Held in a ref so changing a slider does not tear down and restart the render loop.
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    if (!analyzerRef.current) {
      analyzerRef.current = new SpectrumAnalyzer(engine?.sampleRate() ?? 44_100);
    } else if (engine) {
      analyzerRef.current.setSampleRate(engine.sampleRate());
    }
    const analyzer = analyzerRef.current;

    let frame = 0;
    let previous = performance.now();
    const styles = getComputedStyle(canvas);
    const accent = styles.getPropertyValue('--accent').trim() || '#ff4d00';
    const cap = styles.getPropertyValue('--ink').trim() || '#f2f0eb';

    const render = (nowMs: number) => {
      const dt = Math.min((nowMs - previous) / 1_000, 0.1);
      previous = nowMs;

      const magnitudes = playing ? engine?.readSpectrum() : null;
      if (magnitudes) analyzer.update(magnitudes, paramsRef.current, dt);
      else analyzer.decay(dt);

      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (width > 0 && height > 0) {
        if (canvas.width !== Math.round(width * ratio)) {
          canvas.width = Math.round(width * ratio);
          canvas.height = Math.round(height * ratio);
        }
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, width, height);

        // Spread across the full width: past BAR_COUNT the bars widen rather than
        // leaving the right-hand side of the strip blank.
        const { columns, slot, barWidth } = columnLayout(width, COLUMN_PITCH, BAR_COUNT);
        const bars = toDisplayColumns(analyzer.bars, columns, paramsRef.current.symmetric);
        const peaks = toDisplayColumns(analyzer.peaks, columns, paramsRef.current.symmetric);
        const usable = height - 2;

        for (let column = 0; column < columns; column += 1) {
          const x = column * slot;
          // Below the floor a bar would still paint its 1px minimum, drawing a dotted
          // rule across the strip whenever the track is silent or paused.
          if (bars[column]! > 0.005) {
            const barHeight = Math.max(1, bars[column]! * usable);
            context.fillStyle = accent;
            context.globalAlpha = 0.35 + 0.65 * bars[column]!;
            context.fillRect(x, height - barHeight, barWidth, barHeight);
          }

          // Peak-hold caps: the "freeze then drop" marker from spectrum.rs. Drawn in
          // the dock as well as on the track page — they are the most legible part of
          // the analyser at a glance. Skipped near zero, otherwise silence draws a
          // dotted rule along the floor.
          if (peaks[column]! > 0.01) {
            context.globalAlpha = 0.9;
            context.fillStyle = cap;
            context.fillRect(x, height - peaks[column]! * usable, barWidth, 1.5);
          }
        }
        context.globalAlpha = 1;
      }

      frame = requestAnimationFrame(render);
    };

    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [compact, engine, playing]);

  return (
    <canvas
      ref={canvasRef}
      className={compact ? 'spectrum-strip is-compact' : 'spectrum-strip'}
      aria-hidden="true"
    />
  );
}
